import { Address } from "@redotech/account-common/address";
import {
  DropoffEligibilityAddress,
  DropoffRateOption,
} from "@redotech/merchant-sdk/rpc/schema/get-dropoff-options";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { useQuery } from "@tanstack/react-query";

interface DropoffOptionsResult {
  dropoffOptions: DropoffRateOption[];
  dropoffEnabledForTeam: boolean;
}

interface UseDropoffOptionsParams {
  teamId: string;
  returnId: string;
  fromCountry: string | null | undefined;
  merchantAddress: Address | null | undefined;
  enabled?: boolean;
}

function toEligibilityAddress(
  address: Address | null | undefined,
): DropoffEligibilityAddress | undefined {
  if (!address) {
    return undefined;
  }
  return {
    country: address.country,
    returnsCenterId: address.returnsCenterId,
    blueYonderLocationId: address.blueYonderLocationId,
    externalReturnHub: address.externalReturnHub,
  };
}

export function useDropoffOptions({
  teamId,
  returnId,
  fromCountry,
  merchantAddress,
  enabled = true,
}: UseDropoffOptionsParams) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const eligibilityAddress = toEligibilityAddress(merchantAddress);

  return useQuery<DropoffOptionsResult>({
    queryKey: [
      "dropoffOptions",
      teamId,
      returnId,
      fromCountry ?? null,
      eligibilityAddress ?? null,
    ] as const,
    queryFn: async ({ signal }): Promise<DropoffOptionsResult> => {
      const { dropoffOptions, dropoffEnabledForTeam } =
        await rpcClient.getDropoffOptions(
          {
            teamId,
            returnId,
            fromCountry,
            merchantAddress: eligibilityAddress,
          },
          { signal },
        );
      return { dropoffOptions, dropoffEnabledForTeam };
    },
    enabled: !!teamId && !!returnId && !!rpcClient && enabled,
  });
}
